import { Inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, filter, Observable, Subscription } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { INotificationData } from '../models/notification-data';

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // Every 5 mins

interface WsNewNotification {
  messageType: 'NewNotification';
  content: INotificationData;
}

interface WsNotAuthed {
  messageType: 'NotAuthed';
  content: string;
}

interface WsAuthRefused {
  messageType: 'AuthRefused';
  reason: 'expired' | 'invalid';
}

type WsIncomingMessage = WsNewNotification | WsNotAuthed | WsAuthRefused;

interface WsAuthAction {
  action: 'auth';
  token: string;
}

interface WsReadNotificationAction {
  action: 'readnotification';
  notificationId: string;
}

interface WsHeartbeatAction {
  action: 'heartbeat';
}

type WsOutgoingMessage = WsAuthAction | WsReadNotificationAction | WsHeartbeatAction;

type WsMessage = WsIncomingMessage | WsOutgoingMessage;

@Injectable({ providedIn: 'root' })
export class NotificationDataService implements OnDestroy {

  private readonly notifications$ = new BehaviorSubject<INotificationData[]>([]);
  private socket$!: WebSocketSubject<WsMessage>;
  private socketSubscription!: Subscription;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private authenticated = false;

  constructor(
    @Inject('notificationWebSocketUrl') private readonly webSocketUrl: string,
    @Inject('getAccessToken') private readonly getAccessToken: () => string,
  ) {}

  public connect(): void {
    this.connectInternal();
  }

  private connectInternal(): void {
    this.socket$ = webSocket({
      url: this.webSocketUrl,
      deserializer: (event: MessageEvent) => {
        try {
          return JSON.parse(event.data);
        } catch {
          return null;
        }
      },
      openObserver: {
        next: () => {
          this.authenticate();
          this.startHeartbeat();
        },
      },
    });

    this.socketSubscription = this.socket$
      .pipe(filter((message): message is WsMessage => message != null))
      .subscribe({
        next: (message) => this.handleMessage(message as WsIncomingMessage),
        error: () => {},
      });
  }

  ngOnDestroy(): void {
    this.stopHeartbeat();
    this.socketSubscription?.unsubscribe();
    this.socket$?.complete();
  }

  getNotifications(): Observable<INotificationData[]> {
    return this.notifications$.asObservable();
  }

  setNotifications(notifications: INotificationData[]): void {
    this.notifications$.next(notifications);
  }

  addNotification(notification: INotificationData): void {
    const current = this.notifications$.value;
    this.notifications$.next([notification, ...current]);
  }

  markAsRead(messageId: string): void {
    const current = this.notifications$.value;
    const updated = current.map(notification =>
      notification.messageId === messageId
        ? { ...notification, read: true }
        : notification
    );
    this.notifications$.next(updated);

    if (this.authenticated) {
      this.socket$.next({ action: 'readnotification', notificationId: messageId });
    }
  }

  private authenticate(): void {
    const token = this.getAccessToken();
    if (!token) {
      return;
    }
    this.socket$.next({ action: 'auth', token });
    this.authenticated = true;
  }

  private handleMessage(message: WsIncomingMessage): void {
    switch (message.messageType) {
      case 'NewNotification':
        this.addNotification({ ...message.content, read: false });
        break;

      case 'NotAuthed':
        this.authenticated = false;
        this.authenticate();
        break;

      case 'AuthRefused':
        this.authenticated = false;
        break;

      default:
        break;
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.socket$.next({ action: 'heartbeat' });
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }
}